import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { ColumnHeader, ColumnParams } from './ColumnHeader';
import { s32, colors } from '@ardoq/design-tokens';
import { ComplaintLevel, DataValue, TableColumn } from './types';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  themeQuartz,
  CellClassParams,
  ITooltipParams,
  ModuleRegistry,
  TooltipModule,
  InfiniteRowModelModule,
  ColumnMenuModule,
  GridReadyEvent,
  DefaultMenuItem,
  GridApi,
  IDatasource,
  ScrollApiModule,
  ICellRendererParams,
  CellRendererSelectorResult,
} from 'ag-grid-enterprise';
import { FlexBox } from '@ardoq/layout';
import { Skeleton } from '@ardoq/loader';
import { getHeaderTextWidth } from './utils';
import { type I18nLocale, type WithI18nLocale, t } from '@ardoq/i18n';
import { ButtonType, IconButton } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { PreviewingRows } from './PreviewingRows';

const LoadingCell = () => (
  <FlexBox align="center" height="full">
    <Skeleton />
  </FlexBox>
);

const selectLoadingCell = <T,>({
  data,
}: ICellRendererParams<T>): CellRendererSelectorResult | undefined =>
  data ? undefined : { component: LoadingCell };

ModuleRegistry.registerModules([
  TooltipModule,
  InfiniteRowModelModule,
  ColumnMenuModule,
  ScrollApiModule,
]);

// In e2e tests we set window.__ardoqE2E = true (via a Playwright fixture) so we
// can disable ag-grid column virtualization. Virtualized off-screen headers are
// not in the DOM, which makes role-based locators (e.g. getByRole('columnheader'))
// flaky. Production behavior is unchanged.
type E2EWindow = Window & { __ardoqE2E?: boolean };
const isE2E =
  typeof window !== 'undefined' && (window as E2EWindow).__ardoqE2E === true;

const scrollGrid = (container: HTMLElement | null, direction: 1 | -1) => {
  const viewport = container?.querySelector<HTMLElement>(
    '.ag-center-cols-viewport'
  );
  viewport?.scrollBy({
    left: (direction * viewport.clientWidth) / 2,
    behavior: 'smooth',
  });
};

const MAIN_MENU_ITEMS: DefaultMenuItem[] = [
  'pinSubMenu',
  'separator',
  'autoSizeThis',
  'autoSizeAll',
  'separator',
  'resetColumns',
];

type TableNewParams<T extends Record<string, DataValue>> = {
  i18nLocale: I18nLocale;
  tableId: string;
  header: ReactNode;
  columns: TableColumn[];
  setSelectedColumn: (column: number) => void;
  getComplaint: (
    columnIndex: number | null,
    rowIndex: number | null
  ) => { level: ComplaintLevel; message: string } | null;
  getRowOriginalIndex: (row: T) => number;
  dataLoader: (params: {
    startRow: number;
    endRow: number;
    successCallback(rowsThisBlock: T[], lastRow: number): void;
    errorCallback(errorRows: T[]): void;
    failCallback(): void;
  }) => void;
  /**
   * Identity of what `dataLoader` will fetch. The grid discards its loaded
   * blocks and refetches from the first row whenever this changes, so it must
   * cover every input the loader closes over, and nothing else.
   */
  previewQueryKey: string;
  advancedLabel?: string;
  confirmLabel?: string;
  clickToConfigureLabel?: string;
};

type TableNewContext = WithI18nLocale;

export const TableNew = <T extends Record<string, DataValue>>({
  i18nLocale,
  tableId,
  header,
  columns,
  setSelectedColumn,
  getComplaint,
  dataLoader,
  previewQueryKey,
  getRowOriginalIndex,
  advancedLabel,
  confirmLabel,
  clickToConfigureLabel,
}: TableNewParams<T>): ReactNode => {
  const previouslySelectedColumn = useRef<string | null>(null);
  const gridRef = useRef<GridApi | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selectedColumn = columns.find(column => column.isHighlighted);
    if (!selectedColumn || !gridRef.current) {
      previouslySelectedColumn.current = null;
      return;
    }
    if (previouslySelectedColumn.current === selectedColumn.dataIndex) return;

    gridRef.current.ensureColumnVisible(selectedColumn.dataIndex);
    previouslySelectedColumn.current = selectedColumn.dataIndex;
  }, [columns]);

  useEffect(() => {
    const api = gridRef.current;
    const context = api?.getGridOption('context') as TableNewContext | null;
    if (!api || !context) return;
    context.i18nLocale = i18nLocale;
    api.refreshHeader();
  }, [i18nLocale]);

  const columnDefs: ColDef[] = columns.map((column, index) => ({
    minWidth: index > 0 ? getHeaderTextWidth(column.title) : undefined,
    suppressHeaderContextMenu: index === 0,
    field: column.dataIndex,
    headerComponent: ColumnHeader,
    headerComponentParams: {
      title: column.title,
      config: column.config,
      isReadyForMapping: column.isConfigurableTable,
      hasActiveFilters: column.hasActiveFilters,
      onConfirm: column.onConfirm,
      onFilter: column.onFilter,
      onSelectColumn: () => setSelectedColumn(index),
      isHighlighted: column.isHighlighted,
      complaint: getComplaint(index, null),
      lastColumn: index === columns.length - 1,
      hideConfig: column.hideConfig,
      advancedLabel,
      confirmLabel,
      clickToConfigureLabel,
    } satisfies ColumnParams,
    cellClassRules: {
      'cell-highlighted': () => column.isHighlighted === true,
      'cell-error': (params: CellClassParams<T>) => {
        if (!params.data) return false;
        const rowOriginalIndex = getRowOriginalIndex(params.data);
        return (
          getComplaint(index === 0 ? null : index, rowOriginalIndex)?.level ===
          'error'
        );
      },
      'cell-warn': (params: CellClassParams<T>) => {
        if (!params.data) return false;
        const rowOriginalIndex = getRowOriginalIndex(params.data);
        return (
          getComplaint(index === 0 ? null : index, rowOriginalIndex)?.level ===
          'warn'
        );
      },
    },
    tooltipValueGetter: (params: ITooltipParams<T>): string | undefined => {
      if (!params.data) return undefined;
      const rowOriginalIndex = getRowOriginalIndex(params.data);
      const complaint = getComplaint(
        index === 0 ? null : index,
        rowOriginalIndex
      );
      if (complaint?.message) return complaint.message;
      return params.value !== null ? String(params.value) : undefined;
    },
    mainMenuItems: () => MAIN_MENU_ITEMS,
  }));

  const [rowCount, setRowCount] = useState<number | null>(null);

  const datasource = useMemo<IDatasource>(
    () => ({
      getRows: params =>
        dataLoader({
          ...params,
          successCallback: (rows, lastRow) => {
            setRowCount(lastRow);
            params.successCallback(rows, lastRow);
          },
          errorCallback: errorRows => {
            setRowCount(params.startRow);
            params.successCallback(
              errorRows,
              params.startRow + errorRows.length
            );
          },
        }),
    }),
    // dataLoader is tracked via previewQueryKey: a new loader identity for the same key. While previewQueryKey relies on the same
    // params as the dataLoader, it caches the datasource better because it turns those params to a string and this way you get better caching
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewQueryKey]
  );

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridRef.current = params.api;
  }, []);

  return (
    <FlexBox flexDirection="column" gap="medium">
      <FlexBox align="end" paddingX="large" gap="medium">
        <FlexBox flexDirection="column" flex={1}>
          {header}
        </FlexBox>
        <FlexBox gap="small" align="center">
          {rowCount !== null && (
            <PreviewingRows rowCount={rowCount} i18nLocale={i18nLocale} />
          )}
          <IconButton
            iconName={IconName.CHEVRON_LEFT}
            onClick={() => scrollGrid(gridContainerRef.current, -1)}
            buttonType={ButtonType.TERTIARY}
            label={t(
              i18nLocale,
              'integrations:tabularConfigMapping.scrollLeft',
              'Scroll left'
            )}
          />
          <IconButton
            iconName={IconName.CHEVRON_RIGHT}
            onClick={() => scrollGrid(gridContainerRef.current, 1)}
            buttonType={ButtonType.TERTIARY}
            label={t(
              i18nLocale,
              'integrations:tabularConfigMapping.scrollRight',
              'Scroll right'
            )}
          />
        </FlexBox>
      </FlexBox>
      <AgGridContainer ref={gridContainerRef}>
        <AgGridReact
          key={tableId}
          rowModelType="infinite"
          datasource={datasource}
          cacheBlockSize={300}
          cacheOverflowSize={2}
          maxConcurrentDatasourceRequests={1}
          infiniteInitialRowCount={300}
          theme={themeQuartz.withParams({
            headerVerticalPaddingScale: 0,
            headerBackgroundColor: 'transparent',
          })}
          columnDefs={columnDefs}
          context={{ i18nLocale } satisfies TableNewContext}
          defaultColDef={{
            autoHeaderHeight: true,
            cellRendererSelector: selectLoadingCell,
          }}
          autoSizeStrategy={{
            type: 'fitCellContents',
            skipHeader: false,
            defaultMaxWidth: 450,
          }}
          tooltipShowDelay={1000}
          onGridReady={onGridReady}
          maintainColumnOrder
          suppressDragLeaveHidesColumns
          suppressColumnVirtualisation={isE2E}
          suppressFieldDotNotation={true}
        />
      </AgGridContainer>
    </FlexBox>
  );
};

const AG_GRID_CONTAINER_HEIGHT = '600px';

const AgGridContainer = styled.div`
  padding-left: ${s32};
  height: ${AG_GRID_CONTAINER_HEIGHT};
  /* AG Grid applies internal padding styles that conflict with our custom header layout.
     Using !important is necessary to override AG Grid's inline styles for proper alignment. */
  .ag-header-cell {
    padding-left: 1px !important;
    padding-right: 5px !important;
  }

  .ag-header-cell-label {
    padding-left: 1px !important;
    padding-right: 5px !important;
  }

  .ag-header-cell,
  .ag-header-group-cell {
    align-items: start;
  }
  .ag-header-cell-comp-wrapper {
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }

  /* Cell highlighting styles */
  .cell-error {
    background-color: ${colors.red50};
  }

  .cell-warn {
    background-color: ${colors.yellow50};
  }

  .cell-highlighted {
    background-color: ${colors.blue50};
  }
`;
